import React, { useCallback, useEffect } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { strings } from '@goodfynd/strings';
import { Loading } from '@goodfynd/ui';
import { LoadingTruck } from '@goodfynd/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import Modal from '../../Modal';

import { StyledButton, StyledButtonContainer, StyledContainer } from './styles';
import type { ModalFormProps } from './types';

export function ModalForm<T extends FieldValues>({
  busy,
  defaultValues,
  onSubmit,
  otherActions,
  renderFields,
  submitButton,
  submitButtonColor,
  submitButtonDisabled,
  submitButtonStyle,
  submitButtonText = strings.labels.submit,
  validationSchema,
  resetOnDefaultChanges = false,
  ...props
}: ModalFormProps<T>) {
  const resolver = zodResolver(validationSchema ?? z.object({}));
  const form = useForm<T>({
    defaultValues,
    resolver,
  });

  useEffect(() => {
    if (resetOnDefaultChanges) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form, resetOnDefaultChanges]);

  const renderSubmitButtonContent = useCallback(() => {
    if (busy) {
      return <Loading type="line" />;
    }

    if (typeof submitButtonText === 'string') {
      return submitButtonText;
    }

    return submitButtonText?.(form.watch);
  }, [busy, form.watch, submitButtonText]);

  return (
    <Modal
      footer={
        <StyledButtonContainer>
          {otherActions}

          {submitButton?.(form) || (
            <StyledButton
              color={submitButtonColor}
              css={submitButtonStyle}
              disabled={busy || submitButtonDisabled}
              id="btnSubmitModal"
              onClick={form.handleSubmit(onSubmit)}
            >
              {renderSubmitButtonContent()}
            </StyledButton>
          )}
        </StyledButtonContainer>
      }
      {...props}
    >
      <StyledContainer>
        {busy ? <LoadingTruck /> : renderFields(form)}
      </StyledContainer>
    </Modal>
  );
}
